import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, Check, MapPin, RotateCcw } from 'lucide-react';
import { createWaitingListEntrySchema } from '@hch/shared';
import { Button } from '~/components/ui/button';
import { Badge, Field, Input, Separator, Textarea } from '~/components/ui/primitives';
import { useJoinWaitingList, useZones } from '~/lib/queries';
import type { BookingDraft } from '../booking-types';

/**
 * RG-05 — l'adresse n'est pas desservie.
 *
 * C'est le seul point du tunnel ou le parcours s'arrete, et il faut donc le traiter
 * avec soin : un ecran qui dit « non » sans rien proposer d'autre est une impasse.
 * Trois choses y remedient, et aucune ne demande d'appel supplementaire a l'API :
 *
 *   1. On montre OU l'on intervient. Sans cette liste, le visiteur doit deviner et
 *      ressaisir des adresses au hasard pour trouver un secteur couvert.
 *   2. On lui permet d'essayer une autre adresse en un clic — son lieu de travail,
 *      celui d'un proche : c'est souvent la que le rendez-vous aura lieu.
 *   3. Apres l'envoi, on dit ce qui va se passer et on laisse deux sorties. Un
 *      message de remerciement sans suite laisse l'utilisateur bloque sur l'etape.
 *
 * Les secteurs sont affiches sous forme de liste, pas de carte : une carte
 * embarquerait Leaflet dans le parcours client, alors qu'il est justement charge a la
 * demande pour ne peser que sur l'administrateur.
 */

/** Liste des secteurs couverts — repond a « alors vous allez ou ? ». */
function SecteursDesservis() {
  const { data: zones } = useZones();

  if (!zones || zones.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Nous intervenons actuellement ici</p>
      <ul className="flex flex-wrap gap-2">
        {zones.map((zone) => (
          <li key={zone.id}>
            <Badge variant="outline" className="gap-1.5 py-1">
              <span
                className="size-2 rounded-full"
                style={{ backgroundColor: zone.color }}
                aria-hidden
              />
              {zone.name}
            </Badge>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">
        Le rendez-vous a lieu la ou se trouve le cycle : une adresse de travail ou celle d'un proche
        situee dans l'un de ces secteurs fonctionne tout aussi bien.
      </p>
    </div>
  );
}

export function OutOfCoverage({
  message,
  address,
  onRetry,
}: {
  message: string;
  address?: BookingDraft['newAddress'];
  /** Vide l'adresse retenue pour permettre une nouvelle recherche immediatement. */
  onRetry: () => void;
}) {
  const [sent, setSent] = React.useState(false);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [contact, setContact] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });

  const join = useJoinWaitingList();

  // --- Apres envoi : dire ce qui va se passer, et ne pas laisser sans issue -----
  if (sent) {
    return (
      <div className="space-y-3">
        <p className="flex items-start gap-2 text-sm text-success">
          <Check className="mt-0.5 size-4 shrink-0" aria-hidden />
          Merci. Vos coordonnees sont enregistrees : nous vous previendrons par courriel des
          l'ouverture de votre secteur.
        </p>

        <p className="text-xs text-muted-foreground">
          Les demandes recues determinent les prochains secteurs ouverts — la votre compte
          reellement. Aucune demarche supplementaire n'est necessaire de votre part.
        </p>

        <Separator />

        <SecteursDesservis />

        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={onRetry}>
            <RotateCcw aria-hidden />
            Essayer une autre adresse
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/">Retour a l'accueil</Link>
          </Button>
        </div>
      </div>
    );
  }

  async function submit() {
    setErrors({});

    if (!address) {
      toast.error("Selectionnez d'abord une adresse dans la liste.");
      return;
    }

    const parsed = createWaitingListEntrySchema.safeParse({
      ...contact,
      phone: contact.phone || null,
      message: contact.message || null,
      street: address.street,
      city: address.city,
      postalCode: address.postalCode,
      lat: address.lat,
      lng: address.lng,
    });

    if (!parsed.success) {
      setErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [issue.path.join('.'), issue.message]),
        ),
      );
      return;
    }

    await join.mutateAsync(parsed.data);
    setSent(true);
  }

  return (
    <div className="space-y-4">
      <p className="flex items-start gap-2 text-sm text-warning">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
        {message}
      </p>

      {/* Affiche AVANT le formulaire : le visiteur doit d'abord savoir ou l'on va,
          car c'est peut-etre la reponse a son besoin. S'inscrire en liste d'attente
          n'est utile que si aucun secteur ne lui convient. */}
      <SecteursDesservis />

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={onRetry}>
          <MapPin aria-hidden />
          Essayer une autre adresse
        </Button>
      </div>

      <Separator />

      <div className="space-y-3">
        <div>
          <p className="text-sm font-medium">Ou faites-vous prevenir a l'ouverture</p>
          <p className="text-xs text-muted-foreground">
            Nous vous ecrivons des qu'un technicien couvre votre secteur. Rien d'autre.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Prenom" htmlFor="wl-firstName" error={errors.firstName} required>
            <Input
              value={contact.firstName}
              onChange={(event) => setContact({ ...contact, firstName: event.target.value })}
              autoComplete="given-name"
            />
          </Field>
          <Field label="Nom" htmlFor="wl-lastName" error={errors.lastName} required>
            <Input
              value={contact.lastName}
              onChange={(event) => setContact({ ...contact, lastName: event.target.value })}
              autoComplete="family-name"
            />
          </Field>
          <Field label="Adresse e-mail" htmlFor="wl-email" error={errors.email} required>
            <Input
              type="email"
              value={contact.email}
              onChange={(event) => setContact({ ...contact, email: event.target.value })}
              autoComplete="email"
            />
          </Field>
          <Field label="Telephone" htmlFor="wl-phone" error={errors.phone} hint="Facultatif">
            <Input
              type="tel"
              value={contact.phone}
              onChange={(event) => setContact({ ...contact, phone: event.target.value })}
              autoComplete="tel"
              placeholder="06 12 34 56 78"
            />
          </Field>
          <Field
            label="Message"
            htmlFor="wl-message"
            hint="Facultatif — nombre de cycles, besoin particulier…"
            className="sm:col-span-2"
          >
            <Textarea
              value={contact.message}
              onChange={(event) => setContact({ ...contact, message: event.target.value })}
              rows={2}
              maxLength={1000}
            />
          </Field>
        </div>

        <Button size="sm" loading={join.isPending} onClick={() => void submit()}>
          Me prevenir a l'ouverture
        </Button>
      </div>
    </div>
  );
}
